// ============================================================================
// EXPENSE FORM MODAL — Add / Edit voucher with lines + allocations
// ============================================================================
// Header (txn no/date, ref, paid-to, notes) + N expense-head lines.
// Each line is DIRECT (manual case pick) or apportioned across a case-group
// (EQUAL / OUTSTANDING / RECOVERED). Server validates Σ allocations = line and
// Σ lines = total.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { expenseService } from 'api/services/expenseService';
import { useExpenseTypes } from 'hooks/api/useExpenseTypes';
import { useCaseGroups } from 'hooks/api/useCaseGroups';
import type {
  ApportionmentShare,
  CreateExpenseRequest,
  Expense,
  ExpenseAllocationMode,
  UpdateExpenseRequest,
} from 'api/types/expense.types';
import CasePickerModal, { type PickedCase } from './CasePickerModal';

// ----------------------------------------------------------------------------
// Form state shape (kept independent of API DTOs so the user can edit freely
// before validation maps it back).
// ----------------------------------------------------------------------------

interface LineAllocationDraft {
  caseId: string;
  caseCode?: string | null;
  caseTypeCode?: string | null;
  customerName?: string | null;
  accountNumber?: string | null;
  /** Stored as string so the user can type freely; parsed at save. */
  amountText: string;
  basisValue: number | null;
}

interface LineDraft {
  /** Local key — uuid-ish; not persisted. */
  localId: string;
  lineSeq: number;
  expenseTypeId: string;
  amountText: string;
  allocationMode: ExpenseAllocationMode;
  caseGroupId: string | null;
  lineNotes: string;
  allocations: LineAllocationDraft[];
  expanded: boolean;
}

interface FormState {
  transactionDate: string;
  refNo: string;
  refDate: string;
  paidTo: string;
  totalText: string;
  notes: string;
  lines: LineDraft[];
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const emptyState = (): FormState => ({
  transactionDate: todayIso(),
  refNo: '',
  refDate: '',
  paidTo: '',
  totalText: '',
  notes: '',
  lines: [newLine(1)],
});

const newLine = (seq: number): LineDraft => ({
  localId: `L-${Math.random().toString(36).slice(2, 9)}`,
  lineSeq: seq,
  expenseTypeId: '',
  amountText: '',
  allocationMode: 'DIRECT',
  caseGroupId: null,
  lineNotes: '',
  allocations: [],
  expanded: true,
});

const parseAmt = (s: string): number => {
  if (!s) return 0;
  const n = Number(String(s).replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (n: number, withSymbol = true) =>
  (withSymbol ? '₹' : '') + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MODE_LABEL: Record<ExpenseAllocationMode, string> = {
  DIRECT: 'Direct',
  EQUAL: 'Group · Equal',
  OUTSTANDING: 'Group · Outstanding',
  RECOVERED: 'Group · Recovery',
};

/** Mutates the first line of `state` according to prefill, if any. */
const applyPrefill = (state: FormState, pf: ExpensePrefill | undefined): FormState => {
  if (!pf || (!pf.caseId && !pf.caseGroupId)) return state;
  const line = state.lines[0];
  if (pf.caseGroupId) {
    line.allocationMode = 'EQUAL';
    line.caseGroupId = pf.caseGroupId;
    line.allocations = [];
  } else if (pf.caseId) {
    line.allocationMode = 'DIRECT';
    line.caseGroupId = null;
    line.allocations = [{
      caseId: pf.caseId,
      caseCode: pf.caseCode,
      caseTypeCode: pf.caseTypeCode,
      customerName: pf.customerName,
      accountNumber: pf.accountNumber,
      amountText: '0',
      basisValue: null,
    }];
  }
  return state;
};

// ----------------------------------------------------------------------------
// Modal
// ----------------------------------------------------------------------------

/** Pre-fill a single line when opening in CREATE mode from a case- or group-context.
 *  Ignored in EDIT mode (the loaded record drives state). */
export interface ExpensePrefill {
  /** Pre-fill a DIRECT line with this single case as the only allocation. */
  caseId?: string;
  caseCode?: string | null;
  caseTypeCode?: string | null;
  customerName?: string | null;
  accountNumber?: string | null;
  /** Pre-fill a group line — mode defaults to EQUAL, user can override. */
  caseGroupId?: string;
}

export interface ExpenseFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: Expense | null;
  /** Optional pre-fill applied on create (sets one initial line). */
  prefill?: ExpensePrefill;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  isOpen, mode, record, prefill, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<FormState>(emptyState());
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Case picker — opened per-line for DIRECT mode.
  const [pickerLineId, setPickerLineId] = useState<string | null>(null);

  // Lookups.
  const { data: expenseTypes } = useExpenseTypes({ page: 1, pageSize: 200, statusCode: 'ACTIVE' });
  const { data: caseGroups } = useCaseGroups({ page: 1, pageSize: 200, statusCode: 'ACTIVE' });

  const expenseTypeOptions = useMemo(
    () =>
      (expenseTypes ?? []).map(t => ({
        value: t.id,
        label: `${t.expenseName}${t.isReimbursable ? '' : ' (non-reimbursable)'}`,
      })),
    [expenseTypes],
  );

  const caseGroupOptions = useMemo(
    () =>
      (caseGroups ?? []).map(g => ({
        value: g.id,
        label: `${g.groupCode}${g.title ? ` — ${g.title}` : ''}`,
      })),
    [caseGroups],
  );

  // Populate form on open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const pf = prefillRef.current;
    const data: FormState = rec
      ? {
          transactionDate: rec.transactionDate ? rec.transactionDate.slice(0, 10) : todayIso(),
          refNo: rec.refNo ?? '',
          refDate: rec.refDate ? rec.refDate.slice(0, 10) : '',
          paidTo: rec.paidTo ?? '',
          totalText: rec.totalAmount != null ? String(rec.totalAmount) : '',
          notes: rec.notes ?? '',
          lines: (rec.lines ?? []).map(l => ({
            localId: `L-${l.id}`,
            lineSeq: l.lineSeq,
            expenseTypeId: l.expenseTypeId,
            amountText: String(l.lineAmount ?? ''),
            allocationMode: l.allocationMode,
            caseGroupId: l.caseGroupId ?? null,
            lineNotes: l.lineNotes ?? '',
            allocations: (l.allocations ?? []).map(a => ({
              caseId: a.caseId,
              caseCode: a.caseCode,
              caseTypeCode: a.caseTypeCode,
              customerName: a.customerName,
              accountNumber: a.accountNumber,
              amountText: String(a.allocatedAmount ?? ''),
              basisValue: a.allocationBasisValue ?? null,
            })),
            expanded: false,
          })),
        }
      : applyPrefill(emptyState(), pf);
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`
      : `${tabId}-form-modal-edit-${currentRecord?.transactionNo || 'new'}`;

    let title: string;
    if (currentMode === 'create') {
      const paidTo = currentFormData.paidTo?.trim();
      const idx = currentCreateInstanceId || '1';
      title = paidTo ? `Add - ${paidTo} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentRecord?.transactionNo || 'Expense'}`;
    }

    const savedFormData: FormState = JSON.parse(JSON.stringify(currentFormData));
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode === 'edit' ? currentRecord?.transactionNo : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // ---------- Line mutations ----------

  const updateLine = useCallback((localId: string, patch: Partial<LineDraft>) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map(l => (l.localId === localId ? { ...l, ...patch } : l)),
    }));
  }, []);

  const removeLine = useCallback((localId: string) => {
    setFormData(prev => {
      const filtered = prev.lines.filter(l => l.localId !== localId);
      return { ...prev, lines: filtered.map((l, i) => ({ ...l, lineSeq: i + 1 })) };
    });
  }, []);

  const addLine = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, newLine(prev.lines.length + 1)],
    }));
  }, []);

  const toggleExpanded = useCallback((localId: string) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map(l => (l.localId === localId ? { ...l, expanded: !l.expanded } : l)),
    }));
  }, []);

  // ---------- Apportionment compute ----------

  const handleCompute = useCallback(async (line: LineDraft) => {
    const amount = parseAmt(line.amountText);
    if (!line.caseGroupId) { notificationService.toast.warning('Pick a case-group first.'); return; }
    if (line.allocationMode === 'DIRECT') return;
    if (amount <= 0) { notificationService.toast.warning('Enter a positive line amount before computing.'); return; }
    try {
      const proposal = await expenseService.computeApportionment({
        caseGroupId: line.caseGroupId,
        allocationMode: line.allocationMode,
        lineAmount: amount,
      });
      const allocs: LineAllocationDraft[] = proposal.shares.map((s: ApportionmentShare) => ({
        caseId: s.caseId,
        caseCode: s.caseCode,
        caseTypeCode: s.caseTypeCode,
        customerName: s.customerName,
        accountNumber: s.accountNumber,
        amountText: String(s.allocatedAmount),
        basisValue: s.allocationBasisValue,
      }));
      updateLine(line.localId, { allocations: allocs });
      notificationService.toast.success(`Computed shares for ${proposal.memberCount} case(s).`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Apportionment failed.', 6000);
    }
  }, [updateLine]);

  // When the user changes mode → DIRECT, clear group + allocations.
  const handleModeChange = useCallback((line: LineDraft, mode: ExpenseAllocationMode) => {
    if (mode === 'DIRECT') {
      updateLine(line.localId, { allocationMode: mode, caseGroupId: null, allocations: [] });
    } else {
      // Switching between group modes — keep group but clear allocations (user must recompute).
      updateLine(line.localId, { allocationMode: mode, allocations: [] });
    }
  }, [updateLine]);

  // Add cases (DIRECT mode) — splits the line amount evenly among new + existing
  // cases if no manual amounts entered yet; otherwise leaves existing alone and
  // appends new cases at 0 for the user to edit.
  const handlePickCases = useCallback((lineId: string, picked: PickedCase[]) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map(l => {
        if (l.localId !== lineId) return l;
        const newAllocs: LineAllocationDraft[] = picked.map(p => ({
          caseId: p.id,
          caseCode: p.caseCode,
          caseTypeCode: p.caseTypeCode,
          customerName: p.customerName,
          accountNumber: p.accountNumber,
          amountText: '0',
          basisValue: null,
        }));
        return { ...l, allocations: [...l.allocations, ...newAllocs] };
      }),
    }));
  }, []);

  const removeAllocation = useCallback((lineId: string, caseId: string) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map(l =>
        l.localId === lineId
          ? { ...l, allocations: l.allocations.filter(a => a.caseId !== caseId) }
          : l,
      ),
    }));
  }, []);

  const updateAllocationAmount = useCallback((lineId: string, caseId: string, text: string) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map(l =>
        l.localId === lineId
          ? {
              ...l,
              allocations: l.allocations.map(a =>
                a.caseId === caseId ? { ...a, amountText: text } : a,
              ),
            }
          : l,
      ),
    }));
  }, []);

  // ---------- Validation / save ----------

  const totalLines = useMemo(
    () => formData.lines.reduce((sum, l) => sum + parseAmt(l.amountText), 0),
    [formData.lines],
  );
  const totalAmount = parseAmt(formData.totalText);

  const validate = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.transactionDate) errors.transactionDate = 'Transaction date is required';
    if (!formData.paidTo.trim()) errors.paidTo = 'Paid-to is required';
    if (totalAmount <= 0) errors.totalText = 'Total amount must be positive';
    if (formData.lines.length === 0) errors.lines = 'Add at least one line';
    formData.lines.forEach((l, idx) => {
      const k = `line-${idx}`;
      if (!l.expenseTypeId) errors[`${k}-head`] = `Line ${idx + 1}: select an expense head`;
      const amt = parseAmt(l.amountText);
      if (amt <= 0) errors[`${k}-amount`] = `Line ${idx + 1}: amount must be positive`;
      if (l.allocationMode !== 'DIRECT' && !l.caseGroupId)
        errors[`${k}-group`] = `Line ${idx + 1}: pick a case-group for ${MODE_LABEL[l.allocationMode]}`;
      if (l.allocations.length === 0)
        errors[`${k}-allocs`] = `Line ${idx + 1}: at least one allocation is required`;
      const aSum = l.allocations.reduce((s, a) => s + parseAmt(a.amountText), 0);
      if (l.allocations.length > 0 && Math.abs(aSum - amt) > 0.01)
        errors[`${k}-sum`] = `Line ${idx + 1}: Σ allocations (${fmtMoney(aSum)}) must equal line amount (${fmtMoney(amt)})`;
    });
    if (Math.abs(totalLines - totalAmount) > 0.01 && totalAmount > 0)
      errors.totalSum = `Σ lines (${fmtMoney(totalLines)}) must equal voucher total (${fmtMoney(totalAmount)})`;
    return errors;
  }, [formData, totalAmount, totalLines]);

  const handleSave = useCallback(() => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      notificationService.toast.error(Object.values(errors)[0]);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payloadLines = formData.lines.map((l, idx) => ({
        lineSeq: idx + 1,
        expenseTypeId: l.expenseTypeId,
        lineAmount: parseAmt(l.amountText),
        allocationMode: l.allocationMode,
        caseGroupId: l.allocationMode === 'DIRECT' ? null : l.caseGroupId,
        lineNotes: l.lineNotes || null,
        allocations: l.allocations.map(a => ({
          caseId: a.caseId,
          allocatedAmount: parseAmt(a.amountText),
          allocationBasisValue: a.basisValue,
        })),
      }));

      if (mode === 'create') {
        const req: CreateExpenseRequest = {
          transactionDate: formData.transactionDate,
          refNo: formData.refNo || null,
          refDate: formData.refDate || null,
          paidTo: formData.paidTo.trim(),
          totalAmount,
          currencyCode: 'INR',
          notes: formData.notes || null,
          lines: payloadLines,
        };
        await expenseService.create(req);
        notificationService.toast.success('Expense recorded.');
      } else if (record) {
        const req: UpdateExpenseRequest = {
          transactionDate: formData.transactionDate,
          refNo: formData.refNo || null,
          refDate: formData.refDate || null,
          paidTo: formData.paidTo.trim(),
          totalAmount,
          currencyCode: 'INR',
          notes: formData.notes || null,
          rowVersion: record.rowVersion,
          lines: payloadLines,
        };
        await expenseService.update(record.id, req);
        notificationService.toast.success('Expense updated.');
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Save failed.', 6000);
    }
  }, [formData, mode, record, totalAmount, onSaved, onClose]);

  // ----------------------------------------------------------------------------

  if (!isOpen) return null;

  const sumOK = Math.abs(totalLines - totalAmount) <= 0.01 && totalAmount > 0;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            <IconSystem.dollarSign size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
            {mode === 'create' ? 'New' : 'Edit'} Expense
            {mode === 'edit' && record?.transactionNo && (
              <span style={{ color: 'var(--text-secondary)', fontWeight: 500, marginLeft: '0.5rem' }}>
                · {record.transactionNo}
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} title="Close" disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* HEADER FIELDS — paired in form-grid-2 wrappers (template convention) */}
          <div className="form-grid-2">
            <div className="form-group" data-field="transactionDate">
              <label className="form-label">Transaction Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('transactionDate') ? ' form-field-error' : ''}`}
                value={formData.transactionDate}
                onChange={e => { clearFieldError('transactionDate'); setFormData(p => ({ ...p, transactionDate: e.target.value })); }}
              />
              {getFieldError('transactionDate') && <div className="form-error-message">{getFieldError('transactionDate')}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Transaction No</label>
              <input
                type="text"
                className="form-input"
                value={mode === 'edit' ? record?.transactionNo ?? '' : 'auto on save'}
                disabled
              />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Reference No</label>
              <input
                type="text"
                className="form-input"
                value={formData.refNo}
                onChange={e => setFormData(p => ({ ...p, refNo: e.target.value }))}
                placeholder="Vendor invoice / receipt no"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Reference Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.refDate}
                onChange={e => setFormData(p => ({ ...p, refDate: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group" data-field="paidTo">
              <label className="form-label">Paid To <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('paidTo') ? ' form-field-error' : ''}`}
                value={formData.paidTo}
                onChange={e => { clearFieldError('paidTo'); setFormData(p => ({ ...p, paidTo: e.target.value })); }}
                placeholder="Vendor / advocate / agency name"
              />
              {getFieldError('paidTo') && <div className="form-error-message">{getFieldError('paidTo')}</div>}
            </div>
            <div className="form-group" data-field="totalText">
              <label className="form-label">Total Amount (₹) <span className="text-danger">*</span></label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`form-input${getFieldError('totalText') ? ' form-field-error' : ''}`}
                value={formData.totalText}
                onChange={e => { clearFieldError('totalText'); setFormData(p => ({ ...p, totalText: e.target.value })); }}
              />
              {getFieldError('totalText') && <div className="form-error-message">{getFieldError('totalText')}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows={2}
              value={formData.notes}
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Voucher-level notes"
            />
          </div>

          {/* LINES */}
          <div style={{ marginTop: '1rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.5rem 0.75rem',
              background: 'var(--bg-secondary)', borderRadius: 6, marginBottom: '0.5rem',
            }}>
              <strong style={{ fontSize: '0.9rem' }}>Expense Heads</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  fontSize: '0.82rem',
                  color: sumOK ? '#16a34a' : '#dc2626',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  Σ lines: {fmtMoney(totalLines)} {totalAmount > 0 && `of ${fmtMoney(totalAmount)}`} {sumOK && '✓'}
                </span>
                <button className="btn btn-secondary" onClick={addLine} type="button">
                  <IconSystem.add size={14} /> Add Head
                </button>
              </div>
            </div>

            {formData.lines.map((line, idx) => {
              const head = expenseTypes?.find(t => t.id === line.expenseTypeId);
              const grp = caseGroups?.find(g => g.id === line.caseGroupId);
              const lineAmt = parseAmt(line.amountText);
              const allocSum = line.allocations.reduce((s, a) => s + parseAmt(a.amountText), 0);
              const allocOK = Math.abs(allocSum - lineAmt) <= 0.01 && lineAmt > 0;
              return (
                <div key={line.localId} style={{
                  border: '1px solid var(--form-border-color)',
                  borderRadius: 6, marginBottom: '0.5rem', overflow: 'hidden',
                }}>
                  {/* COLLAPSED HEADER */}
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleExpanded(line.localId)}
                  >
                    {line.expanded ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
                    <strong style={{ fontSize: '0.85rem' }}>#{idx + 1}</strong>
                    <span style={{ fontSize: '0.85rem' }}>
                      {head ? head.expenseName : <em style={{ color: 'var(--text-secondary)' }}>(no head)</em>}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                      · {fmtMoney(lineAmt)}
                    </span>
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '0.1rem 0.4rem',
                      background: line.allocationMode === 'DIRECT' ? '#dbeafe' : '#fef3c7',
                      color: line.allocationMode === 'DIRECT' ? '#1e40af' : '#92400e',
                      borderRadius: 4,
                    }}>
                      {MODE_LABEL[line.allocationMode]}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      · {line.allocations.length} case{line.allocations.length === 1 ? '' : 's'}
                    </span>
                    <div style={{ flex: 1 }} />
                    <button
                      onClick={e => { e.stopPropagation(); removeLine(line.localId); }}
                      title="Remove line"
                      type="button"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        padding: '0.25rem 0.4rem', color: '#dc2626',
                        display: 'inline-flex', alignItems: 'center',
                      }}
                    >
                      <IconSystem.trash size={16} />
                    </button>
                  </div>

                  {/* EXPANDED BODY */}
                  {line.expanded && (
                    <div style={{ padding: '0.75rem' }}>
                      <div className="form-grid-2" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <div data-field={`line-${idx}-head`}>
                          <SyutSelect
                            label={<>Expense Head <span className="text-danger">*</span></>}
                            options={expenseTypeOptions}
                            value={line.expenseTypeId}
                            onChange={v => updateLine(line.localId, { expenseTypeId: (v as string) || '' })}
                            size="sm"
                            error={getFieldError(`line-${idx}-head`)}
                          />
                        </div>
                        <div className="form-group" data-field={`line-${idx}-amount`} style={{ marginBottom: 0 }}>
                          <label className="form-label">Line Amount (₹) <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className={`form-input${getFieldError(`line-${idx}-amount`) ? ' form-field-error' : ''}`}
                            value={line.amountText}
                            onChange={e => updateLine(line.localId, { amountText: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* MODE */}
                      <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                        <label className="form-label">Apportionment</label>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                          {(['DIRECT', 'EQUAL', 'OUTSTANDING', 'RECOVERED'] as ExpenseAllocationMode[]).map(m => (
                            <label key={m} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`mode-${line.localId}`}
                                value={m}
                                checked={line.allocationMode === m}
                                onChange={() => handleModeChange(line, m)}
                              />
                              {MODE_LABEL[m]}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* GROUP + COMPUTE (for group modes) */}
                      {line.allocationMode !== 'DIRECT' && (
                        <div className="form-grid-2" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <div data-field={`line-${idx}-group`}>
                            <SyutSelect
                              label={<>Case-Group <span className="text-danger">*</span></>}
                              options={caseGroupOptions}
                              value={line.caseGroupId || ''}
                              onChange={v => updateLine(line.localId, { caseGroupId: (v as string) || null, allocations: [] })}
                              size="sm"
                              error={getFieldError(`line-${idx}-group`)}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => handleCompute(line)}
                              disabled={!line.caseGroupId || parseAmt(line.amountText) <= 0}
                            >
                              <IconSystem.refresh size={14} /> Compute Shares
                            </button>
                            {grp && (
                              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                {grp.memberCount} member(s)
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ALLOCATIONS GRID */}
                      <div style={{
                        border: '1px solid var(--border-color)', borderRadius: 4,
                        overflow: 'hidden', marginTop: '0.5rem',
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead style={{ background: 'var(--bg-secondary)' }}>
                            <tr>
                              <th style={thMini('150px')}>Case Code</th>
                              <th style={thMini()}>Customer</th>
                              <th style={thMini('150px')}>Account</th>
                              <th style={{ ...thMini('130px'), textAlign: 'right' }}>Amount (₹)</th>
                              <th style={thMini('40px')}> </th>
                            </tr>
                          </thead>
                          <tbody>
                            {line.allocations.length === 0 && (
                              <tr>
                                <td colSpan={5} style={{ padding: '0.6rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                  {line.allocationMode === 'DIRECT'
                                    ? 'No cases yet. Use "Add Cases" below.'
                                    : 'Pick a group + amount and click Compute Shares.'}
                                </td>
                              </tr>
                            )}
                            {line.allocations.map(a => (
                              <tr key={a.caseId} style={{ borderTop: '1px solid var(--border-color)' }}>
                                <td style={tdMini()}><strong>{a.caseCode ?? a.caseId.slice(0, 8)}</strong></td>
                                <td style={tdMini()}>{a.customerName ?? '—'}</td>
                                <td style={tdMini()}>{a.accountNumber ?? '—'}</td>
                                <td style={{ ...tdMini(), textAlign: 'right' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-input"
                                    style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '0.25rem 0.4rem' }}
                                    value={a.amountText}
                                    onChange={e => updateAllocationAmount(line.localId, a.caseId, e.target.value)}
                                  />
                                </td>
                                <td style={tdMini()}>
                                  <button
                                    type="button"
                                    onClick={() => removeAllocation(line.localId, a.caseId)}
                                    style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer' }}
                                    title="Remove"
                                  >
                                    <IconSystem.x size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {line.allocations.length > 0 && (
                              <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border-color)' }}>
                                <td colSpan={3} style={{ ...tdMini(), fontWeight: 600 }}>
                                  Σ allocations
                                </td>
                                <td style={{
                                  ...tdMini(), textAlign: 'right', fontWeight: 600,
                                  fontVariantNumeric: 'tabular-nums',
                                  color: allocOK ? '#16a34a' : '#dc2626',
                                }}>
                                  {fmtMoney(allocSum)} {allocOK && '✓'}
                                </td>
                                <td style={tdMini()} />
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {line.allocationMode === 'DIRECT' && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => setPickerLineId(line.localId)}
                          >
                            <IconSystem.add size={14} /> Add Cases
                          </button>
                        </div>
                      )}

                      <div className="form-group" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                        <label className="form-label">Line Notes</label>
                        <input
                          type="text"
                          className="form-input"
                          value={line.lineNotes}
                          onChange={e => updateLine(line.localId, { lineNotes: e.target.value })}
                          placeholder="(optional) e.g. computation basis"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'create' ? 'Save Expense' : 'Update Expense'}
          </button>
        </div>
      </div>

      {/* Case picker */}
      <CasePickerModal
        isOpen={!!pickerLineId}
        excludeCaseIds={
          pickerLineId
            ? (formData.lines.find(l => l.localId === pickerLineId)?.allocations.map(a => a.caseId) ?? [])
            : []
        }
        onPick={cases => { if (pickerLineId) handlePickCases(pickerLineId, cases); }}
        onClose={() => setPickerLineId(null)}
      />

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} this expense for ${fmtMoney(totalAmount)}?`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const thMini = (width?: string): React.CSSProperties => ({
  padding: '0.35rem 0.5rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderBottom: '1px solid var(--border-color)',
  whiteSpace: 'nowrap',
  width,
});

const tdMini = (): React.CSSProperties => ({
  padding: '0.3rem 0.5rem',
  verticalAlign: 'middle',
});

export default ExpenseFormModal;
